const defaultApiBase = import.meta.env.VITE_API_BASE_URL ?? "";
const defaultSupabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? "";
const defaultAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
const cookieName = "sb-auth-token";
const refreshMarkerKey = "mvs:teacher-refresh-attempts:v1";

/**
 * 갱신 토큰이 «다시 해도 안 되는» 거절인가 — 그렇다면 재로그인으로 보낸다.
 *
 * 🩸 Codex 검수 #4(2026-10-08 · 그날 09:28 교사 실시간 401 14건의 후보): `error === "invalid_grant"` 만
 *    봤다. Supabase 는 지금 `error_code: "refresh_token_not_found"` 같은 모양으로도 답한다 — 그걸
 *    일시 장애로 분류해 만료 토큰 요청과 갱신을 되풀이했다.
 * 🔑 4xx 인증 거절(400·401·403)은 영구, 네트워크·429·5xx 만 다시 시도한다.
 */
const PERMANENT_REFRESH_CODES = new Set([
  "invalid_grant",
  "refresh_token_not_found",
  "refresh_token_already_used",
  "session_not_found",
  "session_expired",
  "user_not_found",
  "user_banned",
]);

export function isPermanentRefreshRefusal(
  status: number,
  body: { error?: string; error_code?: string; code?: string | number } | null,
): boolean {
  const codes = [body?.error, body?.error_code, typeof body?.code === "string" ? body.code : undefined];
  if (codes.some((code) => code !== undefined && PERMANENT_REFRESH_CODES.has(code))) return true;
  return status === 400 || status === 401 || status === 403;
}

export interface TeacherTokens {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
  expires_in?: number;
  [key: string]: unknown;
}

export class TeacherLoginRequiredError extends Error {
  constructor(message = "teachermate 로그인이 필요해요") {
    super(message);
  }
}

export class TeacherRetryableError extends Error {
  constructor(message = "교사 인증 연결을 다시 확인하고 있어요") {
    super(message);
  }
}

export class TeacherApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

interface LockManagerLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface TeacherApiOptions {
  fetcher?: typeof fetch;
  apiBase?: string;
  supabaseUrl?: string;
  anonKey?: string;
  readTokens?: () => TeacherTokens | null;
  writeTokens?: (tokens: TeacherTokens) => void;
  locks?: LockManagerLike | null;
  storage?: StorageLike | null;
  wait?: (milliseconds: number) => Promise<void>;
}

function validTokens(value: unknown): value is TeacherTokens {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<TeacherTokens>;
  return typeof candidate.access_token === "string" && typeof candidate.refresh_token === "string";
}

function decodeCookieValue(value: string): unknown {
  const decoded = decodeURIComponent(value);
  if (decoded.startsWith("base64-")) {
    const normalized = decoded.slice("base64-".length).replace(/-/gu, "+").replace(/_/gu, "/");
    return JSON.parse(atob(normalized));
  }
  return JSON.parse(decoded);
}

export function readTeacherTokensFromCookie(cookie = document.cookie): TeacherTokens | null {
  const raw = cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${cookieName}=`))
    ?.slice(cookieName.length + 1);
  if (!raw) return null;
  try {
    const parsed = decodeCookieValue(raw);
    return validTokens(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

const sharedCookieDomain = ".teachermate.co.kr";
const sharedCookieMaxAge = 365 * 24 * 60 * 60;

/** 허브 js-cookie 와 같은 인코딩 — 쿠키에 그대로 둬도 되는 글자는 풀어 둔다(4KB 한도 여유). */
function encodeCookieValue(value: string): string {
  return encodeURIComponent(value).replace(/%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/gu, decodeURIComponent);
}

/** 허브 stripIdentities 와 같다 — `user.identities` 는 아무도 안 읽고 공유 쿠키 4KB 를 넘기게 한다. */
function withoutIdentities(tokens: TeacherTokens): TeacherTokens {
  const user = tokens.user as { identities?: unknown } | null | undefined;
  if (!user || typeof user !== "object" || !("identities" in user)) return tokens;
  const { identities: _dropped, ...rest } = user;
  return { ...tokens, user: rest };
}

/**
 * 🩸 교사 토큰 쿠키는 허브가 쓰는 `.teachermate.co.kr` 쿠키 «그 자체» 를 고쳐 써야 한다(2026-10-15 아름학교).
 * Domain 없이 쓰면 host-only 사본이 하나 더 생기고, 읽기는 같은 이름 중 첫 번째(허브의 옛 쿠키)를 집는다 —
 * 그래서 진행판 폴링마다 만료 토큰 401 → 갱신 → 재요청이 무한 반복됐다(401 ×596).
 * - 운영 도메인: 허브와 같은 Domain·수명(365일)으로 쓰고, 이미 생긴 host-only 사본은 지운다.
 * - 로컬: 지금처럼 host-only.
 */
export function teacherCookieWrites(tokens: TeacherTokens, hostname: string, protocol: string): string[] {
  const secure = protocol === "https:" ? "; Secure" : "";
  const value = encodeCookieValue(JSON.stringify(withoutIdentities(tokens)));
  if (!hostname.endsWith("teachermate.co.kr")) {
    return [`${cookieName}=${value}; Path=/; SameSite=Lax${secure}`];
  }
  return [
    `${cookieName}=; Path=/; Max-Age=0`,
    `${cookieName}=${value}; Domain=${sharedCookieDomain}; Path=/; Max-Age=${sharedCookieMaxAge}; SameSite=Lax${secure}`,
  ];
}

export function writeTeacherTokensToCookie(tokens: TeacherTokens): void {
  for (const line of teacherCookieWrites(tokens, window.location.hostname, window.location.protocol)) {
    document.cookie = line;
  }
}

function markerMap(storage: StorageLike): Record<string, string> {
  try {
    const parsed = JSON.parse(storage.getItem(refreshMarkerKey) ?? "{}") as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
    );
  } catch {
    return {};
  }
}

function setMarker(storage: StorageLike | null, id: string, generation: string): void {
  if (!storage) return;
  storage.setItem(refreshMarkerKey, JSON.stringify({ ...markerMap(storage), [id]: generation }));
}

function clearMarker(storage: StorageLike | null, id: string): void {
  if (!storage) return;
  const markers = markerMap(storage);
  delete markers[id];
  storage.setItem(refreshMarkerKey, JSON.stringify(markers));
}

function hasOtherMarker(storage: StorageLike | null, id: string, generation: string): boolean {
  if (!storage) return false;
  return Object.entries(markerMap(storage)).some(([candidate, value]) =>
    candidate !== id && value === generation
  );
}

class RefreshFailure extends Error {
  constructor(readonly invalidGrant: boolean, message: string) {
    super(message);
  }
}

export function createTeacherApi(options: TeacherApiOptions = {}) {
  const fetcher = options.fetcher ?? fetch;
  const apiBase = options.apiBase ?? defaultApiBase;
  const supabaseUrl = (options.supabaseUrl ?? defaultSupabaseUrl).replace(/\/$/u, "");
  const anonKey = options.anonKey ?? defaultAnonKey;
  const readTokens = options.readTokens ?? (() => readTeacherTokensFromCookie());
  const writeTokens = options.writeTokens ?? writeTeacherTokensToCookie;
  const locks = options.locks === undefined
    ? (typeof navigator !== "undefined" && "locks" in navigator
        ? navigator.locks as unknown as LockManagerLike
        : null)
    : options.locks;
  const storage = options.storage === undefined
    ? (typeof localStorage === "undefined" ? null : localStorage)
    : options.storage;
  const wait = options.wait ?? ((milliseconds: number) =>
    new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds)));
  const contextId = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
  let refreshPromise: Promise<TeacherTokens> | null = null;

  const refresh = async (tokens: TeacherTokens): Promise<TeacherTokens> => {
    if (!supabaseUrl) throw new TeacherRetryableError("Supabase 인증 주소가 설정되지 않았어요");
    let response: Response;
    try {
      response = await fetcher(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(anonKey ? { apikey: anonKey } : {}),
        },
        body: JSON.stringify({ refresh_token: tokens.refresh_token }),
      });
    } catch {
      throw new RefreshFailure(false, "교사 인증 서버에 연결하지 못했습니다.");
    }
    const body = await response.json().catch(() => null) as
      | Partial<TeacherTokens> & { error?: string; error_code?: string; code?: string | number; error_description?: string; msg?: string }
      | null;
    if (!response.ok || !validTokens(body)) {
      throw new RefreshFailure(
        isPermanentRefreshRefusal(response.status, body),
        body?.error_description ?? body?.msg ?? "교사 인증을 갱신하지 못했습니다.",
      );
    }
    const next: TeacherTokens = {
      ...tokens,
      ...body,
      refresh_token: body.refresh_token || tokens.refresh_token,
    };
    writeTokens(next);
    return next;
  };

  const refreshWithLock = (usedAccessToken: string): Promise<TeacherTokens> =>
    locks!.request("mvs-teacher-refresh", async () => {
      const current = readTokens();
      if (!current) throw new TeacherLoginRequiredError();
      if (current.access_token !== usedAccessToken) return current;
      try {
        return await refresh(current);
      } catch (error) {
        const latest = readTokens();
        if (latest && latest.access_token !== usedAccessToken) return latest;
        if (error instanceof RefreshFailure && error.invalidGrant) {
          throw new TeacherLoginRequiredError();
        }
        throw new TeacherRetryableError();
      }
    });

  const waitForCompetingRefresh = async (usedAccessToken: string): Promise<TeacherTokens> => {
    while (true) {
      const latest = readTokens();
      if (latest && latest.access_token !== usedAccessToken) return latest;
      if (!hasOtherMarker(storage, contextId, usedAccessToken)) {
        throw new TeacherRetryableError();
      }
      await wait(50);
    }
  };

  const refreshWithoutLock = async (usedAccessToken: string): Promise<TeacherTokens> => {
    const current = readTokens();
    if (!current) throw new TeacherLoginRequiredError();
    if (current.access_token !== usedAccessToken) return current;
    setMarker(storage, contextId, usedAccessToken);
    try {
      return await refresh(current);
    } catch (error) {
      const latest = readTokens();
      if (latest && latest.access_token !== usedAccessToken) return latest;
      clearMarker(storage, contextId);
      if (hasOtherMarker(storage, contextId, usedAccessToken)) {
        return await waitForCompetingRefresh(usedAccessToken);
      }
      if (error instanceof RefreshFailure && error.invalidGrant) {
        throw new TeacherLoginRequiredError();
      }
      throw new TeacherRetryableError();
    } finally {
      clearMarker(storage, contextId);
    }
  };

  const recover = (usedAccessToken: string): Promise<TeacherTokens> => {
    const current = readTokens();
    if (!current) return Promise.reject(new TeacherLoginRequiredError());
    if (current.access_token !== usedAccessToken) return Promise.resolve(current);
    if (!refreshPromise) {
      refreshPromise = (locks
        ? refreshWithLock(usedAccessToken)
        : refreshWithoutLock(usedAccessToken)
      ).finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  };

  const send = (path: string, init: RequestInit, accessToken: string): Promise<Response> => {
    const headers = new Headers(init.headers);
    headers.set("authorization", `Bearer ${accessToken}`);
    if (init.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }
    return fetcher(`${apiBase}${path}`, { ...init, headers });
  };

  const parse = async <T>(response: Response): Promise<T> => {
    const body = await response.json().catch(() => null) as
      | T
      | { error?: { code?: string; message?: string } }
      | null;
    if (!response.ok) {
      const error = body && typeof body === "object" && "error" in body ? body.error : undefined;
      if (response.status === 401) throw new TeacherLoginRequiredError();
      throw new TeacherApiError(
        response.status,
        error?.code ?? "INTERNAL",
        error?.message ?? "요청을 처리하지 못했습니다.",
      );
    }
    return body as T;
  };

  return async function teacherRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
    const tokens = readTokens();
    if (!tokens) throw new TeacherLoginRequiredError();
    const first = await send(path, init, tokens.access_token);
    if (first.status !== 401) return parse<T>(first);
    const recovered = await recover(tokens.access_token);
    const retry = await send(path, init, recovered.access_token);
    return parse<T>(retry);
  };
}

export const teacherRequest = createTeacherApi();
